import { useQuery } from "@apollo/react-hooks"
import HtmlParser from "react-html-parser"
import { useEffect, useState } from "react"
import { useRouter } from "next/router"

import {
	imagesOfType,
	CATEGORY_SELECTION,
	SVOD_LARGE,
} from "lacinetek-common/src/images"
import {
	SVOD2_LINK_BUTTON,
	SVOD_BLOCK_TEXT,
	SVOD_IMAGE_TITLE,
	TEXT_BUTTON,
	metadata
} from "lacinetek-common/src/metadata"

import { useCountry } from "lib/country"
import { FETCH_SVOD_OFFERS } from "lib/graphql"
import { useTranslation } from "lib/i18n/translation"
import { useLanguages } from "lib/languages"
import Gtm from "lib/gtm"
import { categoryViewedDataLayer } from "lib/gtm/interactions"
import { useCart } from "lib/cart"
import { getOfferImage, isSubscriptionOffer } from "tools/subscription"

import { SubscribeButton } from "components/common/subscribe-button"
import Image from "components/common/image"
import { FooterOffer } from "components/subscription/footer-offer"
import { BlockSpecialRate } from "components/subscription/block-special-rate"
import { BlockOfferLacinetek } from "components/subscription/block-offer-lacinetek"
import { NavOffers } from "components/offers/nav-offers"
import classNames from "classnames"

const SVOD_TEXT_PROMO = "cartouche promotionnel"

const sendAndFormatOffersForGtm = (availableOffers, cartSubscriptions, countrySubscriptions) => {
	const displayedOffers = [
		...availableOffers.main.children.items,
		...availableOffers.partner.children.items,
		...availableOffers.annexe.children.items
	]

	displayedOffers.forEach(offer => {
		const targetCountrySubscription = countrySubscriptions.find(sub => sub.subscriptionId == metadata(offer, SVOD2_LINK_BUTTON))
		const targetCartSubscription = cartSubscriptions.find(sub => sub.id == targetCountrySubscription?.id)
		const price = targetCartSubscription ? targetCartSubscription.extension.accessModes[0].price : 0

		categoryViewedDataLayer({ offer, options: { isGift: false, price }})
	})
}

const Subscription = () => {
	const { c } = useTranslation()
	const { current } = useLanguages()
	const { country: { isoCode, subscriptions: countrySubscriptions } } = useCountry()
	const { subscriptions: cartSubscriptions } = useCart()
	const { data, loading, error, refetch } = useQuery(FETCH_SVOD_OFFERS, {
		variables: {
			query: `nos-offres-${isoCode}`
		}
	})
	const router = useRouter()
	const url = router ? router.asPath : null
	const [bannerImg, setBannerImg] = useState()
	const [svodOffers, setSvodOffers] = useState()
	const [availableOffers, setAvailableOffers] = useState()

	useEffect(() => {
		if (!loading) {
			refetch()
		}
	}, [current])

	useEffect(() => {
		if (data?.cms.categories.items[0].children.items.length > 0) {
			const { children: { items: offers } } = data.cms.categories.items[0]
			const subscriptionOffers = offers.find(offer => isSubscriptionOffer(offer))

			setSvodOffers(subscriptionOffers)

			setAvailableOffers({
				main: subscriptionOffers?.children.items.find(item => item.name.match(/principal/gi)),
				partner: subscriptionOffers?.children.items.find(item => item.name.match(/partenaire/gi)),
				annexe: subscriptionOffers?.children.items.find(item => item.name.match(/annexe/gi))
			})

			setBannerImg(subscriptionOffers ? imagesOfType(subscriptionOffers, CATEGORY_SELECTION)[0] : null)
		}
	}, [data])

	useEffect(() => {
		if (availableOffers && availableOffers.main && cartSubscriptions && countrySubscriptions) {
			console.log(availableOffers)
			sendAndFormatOffersForGtm(availableOffers, cartSubscriptions, countrySubscriptions)
		}
	}, [availableOffers, cartSubscriptions, countrySubscriptions])

	if (loading) {
		return (
			<div className="is-loading centered" />
		)
	}

	return (
		<>
			<Gtm pageType="Page offres abonnements" pagePath={url} pageTitle="Nos offres d'abonnement" />
			{
				!!bannerImg
				&& <section className="focus full no-fx">
					<figure className="cover full">
						<picture>
							<Image
								src={bannerImg}
								alt={svodOffers?.descriptionShort}
							/>
						</picture>
					</figure>
				</section>
			}
			<section className="svod-color">
				<NavOffers />
				<h1 className="main-title">{c("lacinetek-subscription")}</h1>
				<p>{c("subscription-description")}</p>
				{
					availableOffers?.main
					&& <div className="offers blocks">
						{
							availableOffers.main.children.items.map(item =>
								<div className="item full" key={`main-offer-${item.id}`}>
									<div className="item-content">
										{
											metadata(item, SVOD_TEXT_PROMO)
											&& <div className="alert block promo">
												{HtmlParser(metadata(item, SVOD_TEXT_PROMO))}
											</div>
										}
										<figure className={classNames(
											"svod-ratio",
											{ "large": item.images.some(img => img.type === SVOD_LARGE) }
										)}>
											<Image
												src={getOfferImage(item)}
												alt={item.descriptionShort}
											/>
										</figure>
										<div className="infos">
											<h3 className="title">
												{HtmlParser(item.name.replace(/\\n/g, "<br />"))}
												{
													metadata(item, SVOD_IMAGE_TITLE)
													&& <small>
														{HtmlParser(metadata(item, SVOD_IMAGE_TITLE))}
													</small>
												}
											</h3>
											<p className="price">
												{HtmlParser(item.descriptionShort)}
												{
													metadata(item, SVOD_BLOCK_TEXT)
													&& HtmlParser(metadata(item, SVOD_BLOCK_TEXT))
												}
											</p>
										</div>
										{
											metadata(item, SVOD2_LINK_BUTTON)
											&& <SubscribeButton
												subscriptionId={metadata(item, SVOD2_LINK_BUTTON)}
												text={metadata(item, TEXT_BUTTON)}
												full
											/>
										}
										<div className="description">
											{HtmlParser(item.description)}
										</div>
									</div>
								</div>
							)
						}
					</div>
				}
				{
					availableOffers?.partner
					&& <div className="offers blocks">
						{
							availableOffers.partner.children.items.map(item =>
								<div className="item full" key={`main-offer-${item.id}`}>
									<div className="item-content">
										{
											metadata(item, SVOD_TEXT_PROMO)
											&& <div className="alert block promo">
												{HtmlParser(metadata(item, SVOD_TEXT_PROMO))}
											</div>
										}
										<div className="inline">
											<figure className="large" style={{ "align-items": "center" }}>
												<Image
													src={getOfferImage(item)}
													alt={item.descriptionShort}
												/>
											</figure>
											<div className="infos">
												<h3 className="title">{item.name}</h3>
												<div className="description">
													{HtmlParser(metadata(item, SVOD_BLOCK_TEXT))}
												</div>
												<p className="price">{HtmlParser(item.description)}</p>
											</div>
										</div>
										{
											metadata(item, SVOD2_LINK_BUTTON)
											&& <SubscribeButton
												subscriptionId={metadata(item, SVOD2_LINK_BUTTON)}
												text={metadata(item, TEXT_BUTTON)}
												full
											/>
										}
									</div>
								</div>
							)
						}
					</div>
				}
				<div className="offers blocks">
					<BlockOfferLacinetek />
					{
						availableOffers?.annexe?.children.items.map(item =>
							<div key={item.id} className="item">
								<div className="item-content">
									<div className="infos">
										<h3 className="title">{item.name}<br /><strong>{item.descriptionShort}</strong></h3>
										<p className="price">{HtmlParser(item.description)}</p>
									</div>
									{
										metadata(item, SVOD2_LINK_BUTTON)
										&& <SubscribeButton
											subscriptionId={metadata(item, SVOD2_LINK_BUTTON)}
											text={metadata(item, TEXT_BUTTON)}
											full
										/>
									}
								</div>
							</div>
						)
					}
					<BlockSpecialRate />
				</div>
				<FooterOffer />
			</section>
		</>
	)
}

export default Subscription
